// Groups (circles + networks) query layer. Circles and Networks share the
// same backend tables and a common adapter shape (`GroupSummary` / `GroupDetail`),
// so one module covers both. The key factory branches on `kind: 'circle' | 'network'`
// where it matters; primitive endpoints (viewer-role, my-pending-requests) share a
// single key.
//
// No 'use client': hooks are React hooks but called from client components.
// Server pages import the keys for prefetch wiring.

import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import type { GroupId, UserId } from '@leanin/shared/ids';
import { getApiClient } from '../api-client';
import { throwApiError } from '../api-error';
import { eventsKeys } from './events';
import { QUERY_KEY_REGISTRY, trimLower } from './normalize';

export type GroupKind = 'circle' | 'network';

// Group kinds that can carry a brand image (logo and/or cover). Spaces are
// topics, not brands, and only support `cover` — the tonal monogram is the
// right mark for a topic. Circles only support `cover` for now.
export type GroupImageKind = 'circle' | 'network' | 'space';
export type GroupImageField = 'logo' | 'cover';
export type GroupImageContentType = 'image/jpeg' | 'image/png' | 'image/webp';

// Which fields each kind supports. Mirrors the server-side authoritative
// table in `apps/server/src/groups/service/logo-upload.ts`
// (`ALLOWED_FIELDS_BY_KIND`). UI consumers call `groupImageSupports(kind,
// field)` to decide whether to render a given upload control at all; the
// server still rejects cross-kind misuse with a 422 if a client tries to
// POST one anyway.
const ALLOWED_IMAGE_FIELDS_BY_KIND: Record<
  GroupImageKind,
  ReadonlyArray<GroupImageField>
> = {
  circle: ['cover'],
  network: ['cover', 'logo'],
  space: ['cover'],
};

/** Whether a given (kind, field) is supported. Use at render time to gate
 *  controls — e.g. don't render the "Upload logo" CTA for spaces. */
export function groupImageSupports(
  kind: GroupImageKind,
  field: GroupImageField,
): boolean {
  return ALLOWED_IMAGE_FIELDS_BY_KIND[kind].includes(field);
}

export type GroupSummary = components['schemas']['GroupSummary'];
export type GroupDetail = components['schemas']['GroupDetail'];
export type GroupMemberView = components['schemas']['GroupMemberView'];
export type NetworkLeaderView = components['schemas']['NetworkLeaderView'];
export type GroupHealthView = components['schemas']['GroupHealthView'];
export type EventSummary = components['schemas']['EventSummary'];
export type ViewerGroupRole = components['schemas']['ViewerGroupRole'];
// Unified membership-request row (D-CONTRACT-5). One row carries a `direction`
// discriminant: `invite` (leader/system → subject) or `request` (subject →
// leaders). The legacy `JoinRequestView` is gone; join-request consumers read
// the `direction='request'` slice of this shape.
export type MembershipRequestView = components['schemas']['MembershipRequestView'];
export type MembershipRequestDirection = MembershipRequestView['direction'];
export type MembershipRequestStatus = MembershipRequestView['status'];
export type InvitableMemberView = components['schemas']['InvitableMemberView'];
// Typeahead row: the person plus their relationship to the target group
// (invitable / already a member / already has a pending invite). The picker
// keeps non-invitable rows visible but non-selectable, flagging their status.
export type InvitableMemberHit = components['schemas']['InvitableMemberHit'];
export type InvitableMemberRelationship =
  components['schemas']['InvitableMemberRelationship'];

// Mirrors the server's `InviteEmailStatus` discriminated union returned by
// GET /{kind}/{id}/invite-email-status. Modeled locally because the generated
// OpenAPI types haven't caught up yet — the codegen pass on push will regenerate
// them, at which point this can collapse to `components['schemas']['InviteEmailStatus']`.
export type PendingInviteSummary = {
  readonly inviteId: string;
  readonly invitedEmail: string;
  readonly roleOnAccept: 'member' | 'leader';
  readonly createdAt: string;
  readonly expiresAt: string | null;
  // Null for platform/system-issued invites — no human inviter.
  readonly invitedBy: {
    readonly userId: string;
    readonly firstName: string;
    readonly lastName: string;
  } | null;
};

export type InviteEmailStatus =
  | { readonly status: 'invalid_email' }
  | { readonly status: 'off_platform' }
  | {
      readonly status: 'on_platform_invitable';
      readonly member: InvitableMemberView;
    }
  | {
      readonly status: 'already_member';
      readonly member: InvitableMemberView;
      readonly role: 'member' | 'leader';
    }
  | {
      readonly status: 'pending_invite';
      readonly invite: PendingInviteSummary;
    }
  | {
      readonly status: 'pending_request';
      readonly requestedAt: string;
    };

// ---- List args ----

export type ListCirclesArgs = {
  readonly q?: string;
  readonly topic?: string;
  readonly format?: 'in_person' | 'virtual' | 'hybrid';
  readonly language?: string;
  readonly placeText?: string;
  readonly placeCountryCode?: string;
  readonly sort?: 'recent' | 'name' | 'recommended';
  readonly pageSize?: number;
};

export type NetworkScope = 'all' | 'mine';

export type ListNetworksArgs = {
  readonly q?: string;
  readonly pageSize?: number;
  readonly region?: string;
  readonly scope?: NetworkScope;
  /** Only honored by the server when `scope === 'mine'`. Surfaces archived
   *  networks the viewer leads, so they appear under the Mine grid. */
  readonly includeArchived?: boolean;
};

// ---- Query keys + leaf query factories ----
//
// Inner nodes (`all`, `lists()`, `*.all`) stay as bare `as const` key arrays —
// they're the prefix lattice invalidation partial-matches against. Every LEAF
// returns a `queryOptions`/`infiniteQueryOptions` object: its cache key and its
// fetch input are built ADJACENTLY from one normalized `args` object, so the two
// can never drift (react-query-options-migration decisions 1–4).

export const groupKeys = {
  all: ['groups'] as const,

  circle: {
    all: ['groups', 'circle'] as const,
    lists: () => [...groupKeys.circle.all, 'list'] as const,

    // Infinite browse list. cursor injected via pageParam, never in the key.
    list: (raw: ListCirclesArgs = {}) => {
      const args: ListCirclesArgs = {
        q: raw.q?.trim() || undefined,
        topic: raw.topic,
        format: raw.format,
        language: raw.language,
        placeText: raw.placeText,
        placeCountryCode: raw.placeCountryCode,
        sort: raw.sort,
        pageSize: raw.pageSize,
      };
      return infiniteQueryOptions({
        queryKey: [...groupKeys.circle.lists(), args] as const,
        queryFn: ({ pageParam }) =>
          fetchCircles({ ...args, cursor: pageParam ?? undefined }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      });
    },

    // The viewer's own circles. pageSize is in the key so the page-6 profile
    // preview and the page-100 picker are distinct cache entries (mirrors
    // `network.mine` / `members`). Invalidate every variant via the opts-less
    // prefix `[...circle.all, 'mine']`.
    mine: (raw?: { includeArchived?: boolean; pageSize?: number }) => {
      const args = {
        includeArchived: raw?.includeArchived ?? false,
        pageSize: raw?.pageSize ?? null,
      };
      return queryOptions({
        queryKey: [...groupKeys.circle.all, 'mine', args] as const,
        queryFn: () =>
          fetchMyCircles({
            includeArchived: args.includeArchived,
            ...(args.pageSize !== null ? { pageSize: args.pageSize } : {}),
          }),
      });
    },

    // Invalidation prefix (inner node) — matches every page/search variant of
    // the server-paginated `invitesPage` leaf below.
    invites: (id: string) =>
      [...groupKeys.circle.all, 'invites', id] as const,

    invitesPage: (id: string, raw?: { page?: number; q?: string }) => {
      const args = { page: raw?.page ?? 1, q: raw?.q?.trim() ?? '' };
      return queryOptions({
        queryKey: [...groupKeys.circle.invites(id), args] as const,
        queryFn: () =>
          fetchGroupInvitesPage({
            groupId: id,
            page: args.page,
            pageSize: GROUP_INVITES_PAGE_SIZE,
            ...(args.q.length > 0 ? { q: args.q } : {}),
          }),
        placeholderData: (prev) => prev,
      });
    },

    detail: (id: string) =>
      queryOptions({
        queryKey: [...groupKeys.circle.all, 'detail', id] as const,
        queryFn: () => fetchCircle(id),
      }),

    locations: () =>
      queryOptions({
        queryKey: [...groupKeys.circle.all, 'locations'] as const,
        queryFn: fetchCircleLocations,
        staleTime: 5 * 60 * 1000,
      }),

    // Feed posts for a circle's detail page are keyed under
    // `feedKeys.list({ scope: 'group:<id>' })`, not here — so the
    // post-mutation invalidation under `feedKeys.lists()` reaches them.
    // Composes the FROZEN shared `eventsKeys.lists()` prefix (owned by the
    // events WU) so circle-event invalidation participates in the events
    // lattice. Do NOT redefine the events prefix here.
    events: (
      id: string,
      raw?: { window?: 'past' | 'upcoming'; pageSize?: number },
    ) => {
      const args = { window: raw?.window, pageSize: raw?.pageSize ?? null };
      return queryOptions({
        queryKey: [...eventsKeys.lists(), 'circle', id, args] as const,
        queryFn: () =>
          fetchCircleEvents({
            groupId: id,
            ...(args.window !== undefined ? { window: args.window } : {}),
            ...(args.pageSize !== null ? { pageSize: args.pageSize } : {}),
          }),
      });
    },

    // Infinite variant of `events` for the GroupEventsTab's load-more roster.
    // cursor injected via pageParam, never in the key. Keyed distinctly from the
    // plain `events` leaf (`'events-infinite'` segment) so the two never share a
    // cache entry; still composes the FROZEN shared `eventsKeys.lists()` prefix
    // so circle-event invalidation reaches it. The plain `events` leaf stays for
    // its existing non-paginated callers.
    eventsInfinite: (
      id: string,
      raw?: { window?: 'past' | 'upcoming'; pageSize?: number },
    ) => {
      const args = { window: raw?.window, pageSize: raw?.pageSize ?? null };
      return infiniteQueryOptions({
        queryKey: [
          ...eventsKeys.lists(),
          'circle',
          'events-infinite',
          id,
          args,
        ] as const,
        queryFn: ({ pageParam }) =>
          fetchCircleEvents({
            groupId: id,
            cursor: pageParam ?? undefined,
            ...(args.window !== undefined ? { window: args.window } : {}),
            ...(args.pageSize !== null ? { pageSize: args.pageSize } : {}),
          }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      });
    },

    health: (id: string) =>
      queryOptions({
        queryKey: [...groupKeys.circle.all, 'health', id] as const,
        queryFn: () => fetchCircleHealth(id),
      }),

    conversation: (id: string) =>
      queryOptions({
        queryKey: [...groupKeys.circle.all, 'conversation', id] as const,
        queryFn: () => fetchCircleConversation(id),
      }),

    joinRequests: (id: string) =>
      queryOptions({
        queryKey: [...groupKeys.circle.all, 'join-requests', id] as const,
        queryFn: () => fetchCircleJoinRequests(id),
      }),

    inviteNote: (id: string) =>
      queryOptions({
        queryKey: [...groupKeys.circle.all, 'invite-note', id] as const,
        queryFn: () => fetchGroupInviteNote({ kind: 'circle', groupId: id }),
      }),

    invitableMembers: (id: string, raw?: { q?: string }) => {
      const args = { q: raw?.q?.trim() || undefined };
      return queryOptions({
        queryKey: [...groupKeys.circle.all, 'invitable', id, args] as const,
        queryFn: () =>
          fetchInvitableMembers({
            kind: 'circle',
            groupId: id,
            ...(args.q !== undefined ? { q: args.q } : {}),
          }),
      });
    },

    inviteEmailStatus: (id: string, email: string) => {
      const normalizedEmail = trimLower(email);
      return queryOptions({
        queryKey: [
          ...groupKeys.circle.all,
          'invite-email-status',
          id,
          normalizedEmail,
        ] as const,
        queryFn: () =>
          fetchInviteEmailStatus({
            kind: 'circle',
            groupId: id,
            email: normalizedEmail,
          }),
      });
    },
  },

  network: {
    all: ['groups', 'network'] as const,
    lists: () => [...groupKeys.network.all, 'list'] as const,

    // Infinite browse list. cursor injected via pageParam, never in the key.
    list: (raw: ListNetworksArgs = {}) => {
      const args: ListNetworksArgs = {
        q: raw.q?.trim() || undefined,
        pageSize: raw.pageSize,
        region: raw.region,
        scope: raw.scope,
        includeArchived: raw.includeArchived,
      };
      return infiniteQueryOptions({
        queryKey: [...groupKeys.network.lists(), args] as const,
        queryFn: ({ pageParam }) =>
          fetchNetworks({ ...args, cursor: pageParam ?? undefined }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
        placeholderData: keepPreviousData,
      });
    },

    // pageSize is part of the key: the resource composer's audience picker
    // loads pageSize=100 while the networks browse page uses the server
    // default. Sharing one entry would let the picker render the browse page's
    // smaller list (and silently drop networks) until a refetch. Invalidate by
    // the opts-less prefix `[...network.all, 'mine']` so every variant refreshes.
    mine: (raw?: { includeArchived?: boolean; pageSize?: number }) => {
      const args = {
        includeArchived: raw?.includeArchived ?? false,
        pageSize: raw?.pageSize ?? null,
      };
      return queryOptions({
        queryKey: [...groupKeys.network.all, 'mine', args] as const,
        queryFn: () =>
          fetchMyNetworks({
            includeArchived: args.includeArchived,
            ...(args.pageSize !== null ? { pageSize: args.pageSize } : {}),
          }),
      });
    },

    // Invalidation prefix (inner node) — matches every page/search variant of
    // the server-paginated `invitesPage` leaf below.
    invites: (id: string) =>
      [...groupKeys.network.all, 'invites', id] as const,

    invitesPage: (id: string, raw?: { page?: number; q?: string }) => {
      const args = { page: raw?.page ?? 1, q: raw?.q?.trim() ?? '' };
      return queryOptions({
        queryKey: [...groupKeys.network.invites(id), args] as const,
        queryFn: () =>
          fetchGroupInvitesPage({
            groupId: id,
            page: args.page,
            pageSize: GROUP_INVITES_PAGE_SIZE,
            ...(args.q.length > 0 ? { q: args.q } : {}),
          }),
        placeholderData: (prev) => prev,
      });
    },

    detail: (id: string) =>
      queryOptions({
        queryKey: [...groupKeys.network.all, 'detail', id] as const,
        queryFn: () => fetchNetwork(id),
      }),

    leaders: (id: string) =>
      queryOptions({
        queryKey: [...groupKeys.network.all, 'leaders', id] as const,
        queryFn: () => fetchNetworkLeaders(id),
      }),

    // Feed posts for a network's detail page are keyed under
    // `feedKeys.list({ scope: 'group:<id>' })` — see the circle.events
    // comment above for rationale.
    // pageSize is part of the key: the network detail page previews 12
    // circles while the admin "Circles" section loads 50 to manage the full
    // set. Without pageSize in the key they share one cache entry, so opening
    // the admin section after the detail page renders the stale 12-item
    // result until the pageSize-50 refetch settles (issue #33). Mirrors the
    // `members` key, which splits on pageSize for the same reason.
    circles: (
      id: string,
      raw?: { includeArchived?: boolean; pageSize?: number },
    ) => {
      const args = {
        includeArchived: raw?.includeArchived ?? false,
        pageSize: raw?.pageSize ?? null,
      };
      return queryOptions({
        queryKey: [...groupKeys.network.all, 'circles', id, args] as const,
        queryFn: () =>
          fetchNetworkCircles({
            networkId: id,
            includeArchived: args.includeArchived,
            ...(args.pageSize !== null ? { pageSize: args.pageSize } : {}),
          }),
      });
    },

    // Infinite variant of `circles` for the public network detail page's
    // load-more roster. cursor injected via pageParam, never in the key. Keyed
    // distinctly from the plain `circles` leaf (`'circles-infinite'`) so the two
    // never share a cache entry; pageSize stays in the key for the same reason
    // as the plain leaf (see its comment). The admin section still uses the
    // plain `circles` leaf until its own fix lands.
    circlesInfinite: (
      id: string,
      raw?: { includeArchived?: boolean; pageSize?: number },
    ) => {
      const args = {
        includeArchived: raw?.includeArchived ?? false,
        pageSize: raw?.pageSize ?? 24,
      };
      return infiniteQueryOptions({
        queryKey: [
          ...groupKeys.network.all,
          'circles-infinite',
          id,
          args,
        ] as const,
        queryFn: ({ pageParam }) =>
          fetchNetworkCircles({
            networkId: id,
            includeArchived: args.includeArchived,
            pageSize: args.pageSize,
            cursor: pageParam ?? undefined,
          }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      });
    },

    // Composes the FROZEN shared `eventsKeys.lists()` prefix (owned by the
    // events WU). Do NOT redefine the events prefix here.
    events: (
      id: string,
      raw?: { window?: 'past' | 'upcoming'; pageSize?: number },
    ) => {
      const args = { window: raw?.window, pageSize: raw?.pageSize ?? null };
      return queryOptions({
        queryKey: [...eventsKeys.lists(), 'network', id, args] as const,
        queryFn: () =>
          fetchNetworkEvents({
            networkId: id,
            ...(args.window !== undefined ? { window: args.window } : {}),
            ...(args.pageSize !== null ? { pageSize: args.pageSize } : {}),
          }),
      });
    },

    // Infinite variant of `events` for the GroupEventsTab's load-more roster.
    // See the `circle.eventsInfinite` comment above for rationale (distinct
    // `'events-infinite'` key segment, cursor via pageParam, composes the
    // FROZEN shared `eventsKeys.lists()` prefix).
    eventsInfinite: (
      id: string,
      raw?: { window?: 'past' | 'upcoming'; pageSize?: number },
    ) => {
      const args = { window: raw?.window, pageSize: raw?.pageSize ?? null };
      return infiniteQueryOptions({
        queryKey: [
          ...eventsKeys.lists(),
          'network',
          'events-infinite',
          id,
          args,
        ] as const,
        queryFn: ({ pageParam }) =>
          fetchNetworkEvents({
            networkId: id,
            cursor: pageParam ?? undefined,
            ...(args.window !== undefined ? { window: args.window } : {}),
            ...(args.pageSize !== null ? { pageSize: args.pageSize } : {}),
          }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      });
    },

    joinRequests: (id: string) =>
      queryOptions({
        queryKey: [...groupKeys.network.all, 'join-requests', id] as const,
        queryFn: () => fetchNetworkJoinRequests(id),
      }),

    inviteNote: (id: string) =>
      queryOptions({
        queryKey: [...groupKeys.network.all, 'invite-note', id] as const,
        queryFn: () => fetchGroupInviteNote({ kind: 'network', groupId: id }),
      }),

    invitableMembers: (id: string, raw?: { q?: string }) => {
      const args = { q: raw?.q?.trim() || undefined };
      return queryOptions({
        queryKey: [...groupKeys.network.all, 'invitable', id, args] as const,
        queryFn: () =>
          fetchInvitableMembers({
            kind: 'network',
            groupId: id,
            ...(args.q !== undefined ? { q: args.q } : {}),
          }),
      });
    },

    inviteEmailStatus: (id: string, email: string) => {
      const normalizedEmail = trimLower(email);
      return queryOptions({
        queryKey: [
          ...groupKeys.network.all,
          'invite-email-status',
          id,
          normalizedEmail,
        ] as const,
        queryFn: () =>
          fetchInviteEmailStatus({
            kind: 'network',
            groupId: id,
            email: normalizedEmail,
          }),
      });
    },
  },

  // Shared (group-id, kind-agnostic) endpoints.
  // Members list lives at /api/v1/groups/{id}/members and works for circles,
  // networks, and spaces alike — one key, regardless of kind.
  // pageSize is part of the key: the admin fetches 100 and the public view
  // paginates at 24, so they must not share a cache entry.
  members: (id: string, raw?: { search?: string; pageSize?: number }) => {
    const args = {
      search: raw?.search ?? '',
      pageSize: raw?.pageSize ?? null,
    };
    return queryOptions({
      queryKey: [...groupKeys.all, 'members', id, args] as const,
      queryFn: () =>
        fetchGroupMembers({
          groupId: id,
          ...(args.search !== '' ? { search: args.search } : {}),
          ...(args.pageSize !== null ? { pageSize: args.pageSize } : {}),
        }),
    });
  },

  viewerRole: (groupId: string) =>
    queryOptions({
      queryKey: [...groupKeys.all, 'viewer-role', groupId] as const,
      queryFn: () => fetchViewerGroupRole(groupId),
    }),

  viewerRolesBatch: (groupIds: readonly string[]) => {
    const sortedIds = [...groupIds].sort();
    return queryOptions({
      queryKey: [...groupKeys.all, 'viewer-roles', sortedIds] as const,
      queryFn: () => fetchViewerGroupRoles(sortedIds),
    });
  },

  // The viewer's own PENDING INBOUND invitations — the durable, query-backed
  // consent surface (D6). A leader/system invited the viewer to a group; the
  // viewer Accepts/Declines from here. Independent of notifications: muting the
  // `circle.invited`/`network.invited` nudge never removes a row from this list.
  myPendingInvites: () =>
    queryOptions({
      queryKey: [...groupKeys.all, 'my-pending-invites'] as const,
      queryFn: fetchMyPendingInvites,
    }),

  leaderForRequest: (groupId: string, kind: GroupKind) =>
    queryOptions({
      queryKey: [...groupKeys.all, 'leader-for-request', groupId, kind] as const,
      queryFn: () => fetchGroupLeaderForRequest(groupId, kind),
      staleTime: 60 * 1000,
    }),
};

// ---- Client fetchers: circles ----

export async function fetchCircles(
  args: ListCirclesArgs & { cursor?: string } = {},
) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/circles', {
    params: {
      query: {
        q: args.q,
        topic: args.topic,
        format: args.format,
        language: args.language,
        placeText: args.placeText,
        placeCountryCode: args.placeCountryCode,
        sort: args.sort,
        cursor: args.cursor,
        pageSize: args.pageSize,
      },
    },
  });
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

export async function fetchMyCircles(args?: {
  pageSize?: number;
  includeArchived?: boolean;
}) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/members/me/circles',
    {
      params: {
        query: {
          pageSize: args?.pageSize,
          ...(args?.includeArchived ? { includeArchived: true } : {}),
        },
      },
    },
  );
  if (response.status === 403 || response.status === 404) {
    return { items: [], nextCursor: null, pageSize: 0 } as NonNullable<typeof data>;
  }
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

export type CircleLocationOption = components['schemas']['CircleLocationOption'];

export async function fetchCircleLocations(): Promise<CircleLocationOption[]> {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/circles/locations');
  if (error !== undefined || data === undefined) throwApiError(error);
  return [...data.items];
}

export function useCircleLocations() {
  return useQuery(groupKeys.circle.locations());
}

export type CirclePreview = components['schemas']['CirclePreview'];
export type CircleDetailResponse = components['schemas']['GetCircleResponse'];

export async function fetchCircle(id: string): Promise<CircleDetailResponse> {
  const client = getApiClient();
  const { data, error, response } = await client.GET('/api/v1/circles/{id}', {
    params: { path: { id } },
  });
  if (error !== undefined || data === undefined) throwApiError(error, response);
  return data;
}

export async function fetchCircleEvents(args: {
  groupId: string;
  pageSize?: number;
  cursor?: string;
  window?: 'past' | 'upcoming';
}) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/circles/{id}/events',
    {
      params: {
        path: { id: args.groupId },
        query: {
          pageSize: args.pageSize,
          cursor: args.cursor,
          window: args.window,
        },
      },
    },
  );
  if (response.status === 403 || response.status === 404) {
    return { items: [], nextCursor: null, pageSize: 0 } as NonNullable<typeof data>;
  }
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

export async function fetchCircleHealth(
  id: string,
): Promise<GroupHealthView | null> {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/circles/{id}/health',
    { params: { path: { id } } },
  );
  if (response.status === 403 || response.status === 404) return null;
  if (error !== undefined || data === undefined) throwApiError(error);
  return data.data;
}

export async function fetchCircleConversation(id: string) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/circles/{id}/conversation',
    { params: { path: { id } } },
  );
  if (response.status === 403 || response.status === 404) return null;
  if (error !== undefined || data === undefined) throwApiError(error);
  return data.data;
}

export async function fetchCircleJoinRequests(id: string) {
  return fetchGroupJoinRequests(id);
}

// Kind-agnostic invite fetchers. The `/circles/{id}/*` and `/networks/{id}/*`
// endpoints expose identical shapes (lifted into shared services in
// `apps/server/.../service/group-invites.ts`); we branch only on the URL.

// Per-(viewer, group) invite-blurb note. Returns the saved string or '' when
// the viewer hasn't saved one yet.
export async function fetchGroupInviteNote(args: {
  kind: GroupKind;
  groupId: string;
}): Promise<string> {
  const client = getApiClient();
  const params = { path: { id: args.groupId } };
  const { data, error } =
    args.kind === 'circle'
      ? await client.GET('/api/v1/circles/{id}/invite-prefs', { params })
      : await client.GET('/api/v1/networks/{id}/invite-prefs', { params });
  if (error !== undefined || data === undefined) throwApiError(error);
  return data.note;
}

export async function fetchInvitableMembers(args: {
  kind: GroupKind;
  groupId: string;
  q?: string;
  email?: string;
  limit?: number;
}): Promise<readonly InvitableMemberHit[]> {
  const client = getApiClient();
  const query = {
    ...(args.q !== undefined ? { q: args.q } : {}),
    ...(args.email !== undefined ? { email: args.email } : {}),
    limit: args.limit,
  };
  if (args.kind === 'circle') {
    const { data, error } = await client.GET(
      '/api/v1/circles/{id}/invitable-members',
      { params: { path: { id: args.groupId }, query } },
    );
    if (error !== undefined || data === undefined) throwApiError(error);
    return data.items;
  }
  const { data, error } = await client.GET(
    '/api/v1/networks/{id}/invitable-members',
    { params: { path: { id: args.groupId }, query } },
  );
  if (error !== undefined || data === undefined) throwApiError(error);
  return data.items;
}

// Per-email invite status. Returns a discriminated union that covers every
// terminal state: invalid_email / off_platform / on_platform_invitable /
// already_member / pending_invite. Drives the modal's per-chip hint + send-
// gating, replacing the prior misuse of `invitable-members?email=…` (which
// conflated blocking states with "off-platform" because they were filtered
// upstream).
//
// The generated openapi-types haven't picked up this endpoint yet, so we use
// an untyped fetch + a locally-modeled shape that mirrors the server's Zod
// schema. The codegen pass on push will regenerate the types; this can then
// switch to a typed `client.GET('/api/v1/{kind}s/{id}/invite-email-status')`.
export async function fetchInviteEmailStatus(args: {
  kind: GroupKind;
  groupId: string;
  email: string;
}): Promise<InviteEmailStatus> {
  const path = `/api/v1/${args.kind}s/${args.groupId}/invite-email-status?email=${encodeURIComponent(args.email)}`;
  const client = getApiClient();
  const untypedGet = client.GET as unknown as (
    p: string,
  ) => Promise<{
    data?: InviteEmailStatus;
    error?: unknown;
    response: Response;
  }>;
  const { data, error } = await untypedGet(path);
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

// Rows-per-page for the admin "Pending invites" table.
export const GROUP_INVITES_PAGE_SIZE = 10;

export type GroupInvitesPage = {
  readonly items: readonly MembershipRequestView[];
  readonly total: number;
};

// One server-paginated page of a group's pending invites (admin "Pending
// invites" card). Scoped to `direction='invite'` + `status='pending'`; the
// server applies offset pagination + name/email search and returns the page
// plus the full matching `total` so the numbered pager can size itself.
// Carries the raw `MembershipRequestView` rows (each OFF-platform — a
// magic-link email — or ON-platform — a person awaiting acceptance). Built to
// scale to thousands of invites per group.
async function fetchGroupInvitesPage(args: {
  groupId: string;
  page: number;
  pageSize: number;
  q?: string;
}): Promise<GroupInvitesPage> {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/groups/{id}/membership-requests',
    {
      params: {
        path: { id: args.groupId },
        query: {
          direction: 'invite',
          status: 'pending',
          page: args.page,
          pageSize: args.pageSize,
          ...(args.q !== undefined && args.q.length > 0 ? { q: args.q } : {}),
        },
      },
    },
  );
  if (response.status === 403 || response.status === 404) {
    return { items: [], total: 0 };
  }
  if (error !== undefined || data === undefined) throwApiError(error);
  return { items: data.items, total: data.total };
}

// ---- Client fetchers: networks ----

export async function fetchNetworks(
  args: ListNetworksArgs & { cursor?: string } = {},
) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/networks', {
    params: {
      query: {
        q: args.q,
        cursor: args.cursor,
        pageSize: args.pageSize,
        region: args.region,
        scope: args.scope,
        ...(args.includeArchived ? { includeArchived: true } : {}),
      },
    },
  });
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

export async function fetchMyNetworks(args?: {
  includeArchived?: boolean;
  pageSize?: number;
}) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/members/me/networks',
    {
      params: {
        query: {
          ...(args?.includeArchived ? { includeArchived: true } : {}),
          ...(args?.pageSize !== undefined ? { pageSize: args.pageSize } : {}),
        },
      },
    },
  );
  if (response.status === 403 || response.status === 404) {
    return { items: [], nextCursor: null, pageSize: 0 } as NonNullable<typeof data>;
  }
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

export async function fetchNetwork(id: string): Promise<GroupDetail> {
  const client = getApiClient();
  const { data, error, response } = await client.GET('/api/v1/networks/{id}', {
    params: { path: { id } },
  });
  if (error !== undefined || data === undefined) throwApiError(error, response);
  return data.data;
}

export async function fetchNetworkLeaders(id: string) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/networks/{id}/leaders',
    { params: { path: { id } } },
  );
  if (response.status === 403 || response.status === 404) {
    return { items: [], nextCursor: null, pageSize: 0 } as NonNullable<typeof data>;
  }
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

export async function fetchNetworkCircles(args: {
  networkId: string;
  pageSize?: number;
  cursor?: string;
  includeArchived?: boolean;
}) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/networks/{id}/circles',
    {
      params: {
        path: { id: args.networkId },
        query: {
          pageSize: args.pageSize,
          cursor: args.cursor,
          ...(args.includeArchived ? { includeArchived: true } : {}),
        },
      },
    },
  );
  if (response.status === 403 || response.status === 404) {
    return { items: [], nextCursor: null, pageSize: 0 } as NonNullable<typeof data>;
  }
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

export async function fetchNetworkEvents(args: {
  networkId: string;
  pageSize?: number;
  cursor?: string;
  window?: 'past' | 'upcoming';
}) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/networks/{id}/events',
    {
      params: {
        path: { id: args.networkId },
        query: {
          pageSize: args.pageSize,
          cursor: args.cursor,
          window: args.window,
        },
      },
    },
  );
  if (response.status === 403 || response.status === 404) {
    return { items: [], nextCursor: null, pageSize: 0 } as NonNullable<typeof data>;
  }
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

export async function fetchNetworkJoinRequests(id: string) {
  return fetchGroupJoinRequests(id);
}

// Pending join-requests for a group's leaders. Unified endpoint, scoped to
// `direction='request'` AND `status='pending'` — withdrawn (`revoked`),
// `declined`, and `accepted` rows are terminal and must never surface in the
// leader's action list (showing them produced NOT_FOUND on Approve). This is
// the single boundary that enforces pending-only; consumers trust it and do
// not re-filter. The server also excludes stale-expired rows and gates the
// read on `inviteMember`. Returns the raw `MembershipRequestView` shape; the
// leader cards read the `subject` snapshot off each row.
export async function fetchGroupJoinRequests(
  id: string,
): Promise<{ items: MembershipRequestView[] }> {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/groups/{id}/membership-requests',
    { params: { path: { id }, query: { direction: 'request', status: 'pending' } } },
  );
  if (response.status === 403 || response.status === 404) {
    return { items: [] as MembershipRequestView[] };
  }
  if (error !== undefined || data === undefined) throwApiError(error);
  return { items: data.items };
}

// ---- Shared (kind-agnostic) ----

// Members listing is now a single endpoint that works across circles, networks
// and spaces. `search` is honored by the server for the latter two; circles
// ignore it. The shape is `GroupMemberView` regardless of kind.
export async function fetchGroupMembers(args: {
  groupId: string;
  pageSize?: number;
  cursor?: string;
  search?: string;
}) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/groups/{id}/members',
    {
      params: {
        path: { id: args.groupId },
        query: {
          pageSize: args.pageSize,
          cursor: args.cursor,
          search: args.search,
        },
      },
    },
  );
  if (response.status === 403 || response.status === 404) {
    return { items: [], nextCursor: null, pageSize: 0 } as NonNullable<typeof data>;
  }
  if (error !== undefined || data === undefined) throwApiError(error);
  return data;
}

export async function fetchViewerGroupRole(
  groupId: string,
): Promise<ViewerGroupRole> {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/groups/{groupId}/viewer-role',
    { params: { path: { groupId } } },
  );
  if (response.status === 403 || response.status === 404) return null;
  if (error !== undefined || data === undefined) throwApiError(error);
  return data.role;
}

export async function fetchViewerGroupRoles(
  groupIds: readonly string[],
): Promise<Map<string, 'leader' | 'member'>> {
  if (groupIds.length === 0) return new Map();
  const client = getApiClient();
  const { data, error } = await client.POST('/api/v1/groups/viewer-roles', {
    body: { groupIds: [...groupIds] },
  });
  if (error !== undefined || data === undefined) throwApiError(error);
  const map = new Map<string, 'leader' | 'member'>();
  for (const [gid, role] of Object.entries(data.roles)) {
    if (role === 'leader' || role === 'member') map.set(gid, role);
  }
  return map;
}

// The viewer's own pending INBOUND invites (D6 consent surface). Self-scoped
// server-side (`subject_user_id = viewer`); returns the raw view rows the
// `MembershipInvitesCard` renders with Accept/Decline.
export async function fetchMyPendingInvites(): Promise<
  readonly MembershipRequestView[]
> {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/members/me/membership-requests',
    { params: { query: { direction: 'invite', status: 'pending' } } },
  );
  if (error !== undefined || data === undefined) return [];
  return data.items;
}

// ---- Hooks: circles ----

export function useCircles(
  args: ListCirclesArgs,
  options?: Omit<
    UseQueryOptions<
      Awaited<ReturnType<typeof fetchCircles>>,
      Error,
      Awaited<ReturnType<typeof fetchCircles>>
    >,
    'queryKey' | 'queryFn'
  >,
) {
  // Flat (non-paginated) read sharing the infinite `circle.list` leaf's key.
  // Source the fetch input from the leaf's normalized terminal args so the key
  // and the request derive from one normalized object (decision 3).
  const leaf = groupKeys.circle.list(args);
  const normalized = leaf.queryKey[leaf.queryKey.length - 1] as ListCirclesArgs;
  return useQuery({
    queryKey: leaf.queryKey,
    queryFn: () => fetchCircles(normalized),
    ...options,
  });
}

export function useInfiniteCircles(args: ListCirclesArgs = {}) {
  return useInfiniteQuery(groupKeys.circle.list(args));
}


export function useCircle(id: string) {
  return useQuery(groupKeys.circle.detail(id));
}

export function useCircleEvents(
  groupId: string,
  args?: { pageSize?: number; window?: 'past' | 'upcoming' },
) {
  return useQuery(groupKeys.circle.events(groupId, args));
}

export function useCircleHealth(id: string) {
  return useQuery(groupKeys.circle.health(id));
}

export function useCircleConversation(id: string) {
  return useQuery(groupKeys.circle.conversation(id));
}

export function useCircleJoinRequests(id: string) {
  return useQuery(groupKeys.circle.joinRequests(id));
}

// ---- Hooks: networks ----

export function useNetworks(args: ListNetworksArgs) {
  // Flat read sharing the infinite `network.list` leaf's key — see `useCircles`.
  const leaf = groupKeys.network.list(args);
  const normalized = leaf.queryKey[
    leaf.queryKey.length - 1
  ] as ListNetworksArgs;
  return useQuery({
    queryKey: leaf.queryKey,
    queryFn: () => fetchNetworks(normalized),
  });
}

export function useInfiniteNetworks(args: ListNetworksArgs = {}) {
  return useInfiniteQuery(groupKeys.network.list(args));
}

export function useMyNetworks(options?: { enabled?: boolean }) {
  return useQuery({
    ...groupKeys.network.mine({ includeArchived: true, pageSize: 100 }),
    ...options,
  });
}

export function useNetwork(
  id: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    ...groupKeys.network.detail(id),
    ...options,
  });
}

export function useNetworkLeaders(id: string) {
  return useQuery(groupKeys.network.leaders(id));
}

export function useNetworkCircles(
  networkId: string,
  args?: { pageSize?: number; includeArchived?: boolean },
) {
  return useQuery(groupKeys.network.circles(networkId, args));
}

export function useInfiniteNetworkCircles(
  networkId: string,
  args?: { pageSize?: number; includeArchived?: boolean },
) {
  return useInfiniteQuery(
    groupKeys.network.circlesInfinite(networkId, args),
  );
}

export function useNetworkEvents(
  networkId: string,
  args?: { pageSize?: number; window?: 'past' | 'upcoming' },
) {
  return useQuery(groupKeys.network.events(networkId, args));
}

// Infinite events for the GroupEventsTab's load-more roster. Dispatches to the
// circle/network infinite leaf by kind — mirroring how the plain events are
// split by kind (`useCircleEvents` / `useNetworkEvents`). The kind-selected leaf
// owns the cache key (its `'circle'`/`'network'` segment keeps the two caches
// distinct); we build one `useInfiniteQuery` config off that key with a single
// `queryFn` that branches the fetcher by kind. This mirrors `useInfiniteGroup
// Members`, which likewise builds its infinite config off the leaf's `.queryKey`
// with its own inline `queryFn` (one call site → Rules of Hooks), sourcing the
// fetch input from the leaf's NORMALIZED terminal args so key and request derive
// from one object (decision 3). A bare `useInfiniteQuery(leaf)` can't resolve the
// union of the two kinds' divergent literal-key tuples, hence the rebuild.
export function useInfiniteGroupEvents(
  kind: GroupKind,
  id: string,
  args?: { pageSize?: number; window?: 'past' | 'upcoming' },
) {
  const leaf =
    kind === 'circle'
      ? groupKeys.circle.eventsInfinite(id, args)
      : groupKeys.network.eventsInfinite(id, args);
  const normalized = leaf.queryKey[leaf.queryKey.length - 1] as {
    window?: 'past' | 'upcoming';
    pageSize: number | null;
  };
  const window = normalized.window;
  const pageSize = normalized.pageSize;
  return useInfiniteQuery({
    queryKey: leaf.queryKey as readonly unknown[],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      kind === 'circle'
        ? fetchCircleEvents({
            groupId: id,
            cursor: pageParam ?? undefined,
            ...(window !== undefined ? { window } : {}),
            ...(pageSize !== null ? { pageSize } : {}),
          })
        : fetchNetworkEvents({
            networkId: id,
            cursor: pageParam ?? undefined,
            ...(window !== undefined ? { window } : {}),
            ...(pageSize !== null ? { pageSize } : {}),
          }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}

export function useNetworkJoinRequests(id: string) {
  return useQuery(groupKeys.network.joinRequests(id));
}

// ---- Shared hooks ----

export function useGroupMembers(
  groupId: string,
  args?: { search?: string; pageSize?: number },
) {
  return useQuery(groupKeys.members(groupId, args));
}

export function useInfiniteGroupMembers(
  groupId: string,
  args?: { search?: string; pageSize?: number; enabled?: boolean },
) {
  // Shares the `members` leaf key so a single invalidation refreshes both the
  // flat and infinite consumers, but pages via `pageParam` — so it builds its
  // own infinite config off the leaf's `.queryKey`. The fetch input is sourced
  // from the leaf's normalized terminal args (decision 3): key and request
  // derive from one normalized object.
  const leaf = groupKeys.members(groupId, {
    search: args?.search,
    pageSize: args?.pageSize,
  });
  const normalized = leaf.queryKey[leaf.queryKey.length - 1] as {
    search: string;
    pageSize: number | null;
  };
  return useInfiniteQuery({
    queryKey: leaf.queryKey,
    queryFn: ({ pageParam }) =>
      fetchGroupMembers({
        groupId,
        cursor: pageParam ?? undefined,
        ...(normalized.search !== '' ? { search: normalized.search } : {}),
        ...(normalized.pageSize !== null
          ? { pageSize: normalized.pageSize }
          : {}),
      }),
    enabled: args?.enabled,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}

export function useViewerGroupRole(groupId: string) {
  return useQuery(groupKeys.viewerRole(groupId));
}

export function useViewerGroupRoles(groupIds: readonly string[]) {
  return useQuery({
    ...groupKeys.viewerRolesBatch(groupIds),
    enabled: groupIds.length > 0,
  });
}

export function useMyPendingInvites() {
  return useQuery(groupKeys.myPendingInvites());
}

// The viewer's pending INBOUND invite for one specific group, if any. Reads
// from the same self-scoped invites list (`useMyPendingInvites`) and narrows
// by `groupId` — so a member/leader (no inbound invite) and a never-invited
// visitor both resolve to `null`. `enabled` gates the underlying fetch off
// for anonymous viewers, who can never hold an invite.
export function useViewerPendingInvite(
  groupId: string,
  options?: { readonly enabled?: boolean },
): MembershipRequestView | null {
  const { data } = useQuery({
    ...groupKeys.myPendingInvites(),
    enabled: options?.enabled ?? true,
  });
  return (data ?? []).find((invite) => invite.groupId === groupId) ?? null;
}

// "Show me the leader of this group" — used by the request-to-join modal to
// surface a face/name on a gated group's preview card. Pulls the first leader
// from the appropriate members list and projects to the small display shape
// the modal uses.
export type GroupLeaderSummary = {
  readonly name: string;
  readonly photoUrl: string | null;
  readonly jobTitle: string | null;
  readonly company: string | null;
};

export async function fetchGroupLeaderForRequest(
  groupId: string,
  kind: GroupKind,
): Promise<GroupLeaderSummary | null> {
  try {
    const result = await fetchGroupMembers({ groupId, pageSize: 50 });
    const items = result.items as readonly GroupMemberView[];
    const leader = items.find((m) => m.role === 'leader');
    if (!leader) return null;
    const fallback = kind === 'circle' ? 'Circle Leader' : 'Network Leader';
    const fullName =
      [leader.firstName, leader.lastName].filter(Boolean).join(' ').trim();
    return {
      name: fullName.length > 0 ? fullName : fallback,
      photoUrl: leader.photoUrl ?? null,
      jobTitle: leader.jobTitle ?? null,
      company: leader.company ?? null,
    };
  } catch {
    return null;
  }
}

export function useGroupLeaderForRequest(
  groupId: string,
  kind: GroupKind,
  enabled = true,
) {
  return useQuery({
    ...groupKeys.leaderForRequest(groupId, kind),
    enabled: enabled && groupId.length > 0,
  });
}

// ---- Query-key registry (JSON-serializability invariant) ----
//
// Every leaf factory is registered with a representative `queryKey` so the
// shared invariant test (`__tests__/query-keys.test.ts`, decision 6) asserts
// each key round-trips through JSON — catching a stray Date / function / Map in
// a key. Inner-node prefix functions (`circle.lists()`, etc.) are not leaves and
// are not registered.
QUERY_KEY_REGISTRY.push(
  { name: 'groupKeys.circle.list', sampleKey: groupKeys.circle.list({ q: ' Sales ', sort: 'recent', pageSize: 24 }).queryKey },
  { name: 'groupKeys.circle.mine', sampleKey: groupKeys.circle.mine({ includeArchived: true, pageSize: 6 }).queryKey },
  { name: 'groupKeys.circle.detail', sampleKey: groupKeys.circle.detail('circle-1').queryKey },
  { name: 'groupKeys.circle.locations', sampleKey: groupKeys.circle.locations().queryKey },
  { name: 'groupKeys.circle.events', sampleKey: groupKeys.circle.events('circle-1', { window: 'upcoming', pageSize: 12 }).queryKey },
  { name: 'groupKeys.circle.eventsInfinite', sampleKey: groupKeys.circle.eventsInfinite('circle-1', { window: 'upcoming', pageSize: 12 }).queryKey },
  { name: 'groupKeys.circle.health', sampleKey: groupKeys.circle.health('circle-1').queryKey },
  { name: 'groupKeys.circle.conversation', sampleKey: groupKeys.circle.conversation('circle-1').queryKey },
  { name: 'groupKeys.circle.joinRequests', sampleKey: groupKeys.circle.joinRequests('circle-1').queryKey },
  { name: 'groupKeys.circle.inviteNote', sampleKey: groupKeys.circle.inviteNote('circle-1').queryKey },
  { name: 'groupKeys.circle.invitableMembers', sampleKey: groupKeys.circle.invitableMembers('circle-1', { q: ' Ada ' }).queryKey },
  { name: 'groupKeys.circle.inviteEmailStatus', sampleKey: groupKeys.circle.inviteEmailStatus('circle-1', ' Ada@Example.com ').queryKey },
  { name: 'groupKeys.circle.invitesPage', sampleKey: groupKeys.circle.invitesPage('circle-1', { page: 2, q: ' ada ' }).queryKey },
  { name: 'groupKeys.network.list', sampleKey: groupKeys.network.list({ q: ' Tech ', scope: 'all', pageSize: 24 }).queryKey },
  { name: 'groupKeys.network.mine', sampleKey: groupKeys.network.mine({ includeArchived: true, pageSize: 100 }).queryKey },
  { name: 'groupKeys.network.detail', sampleKey: groupKeys.network.detail('network-1').queryKey },
  { name: 'groupKeys.network.leaders', sampleKey: groupKeys.network.leaders('network-1').queryKey },
  { name: 'groupKeys.network.circles', sampleKey: groupKeys.network.circles('network-1', { includeArchived: true, pageSize: 50 }).queryKey },
  { name: 'groupKeys.network.circlesInfinite', sampleKey: groupKeys.network.circlesInfinite('network-1', { includeArchived: true, pageSize: 24 }).queryKey },
  { name: 'groupKeys.network.events', sampleKey: groupKeys.network.events('network-1', { window: 'past', pageSize: 12 }).queryKey },
  { name: 'groupKeys.network.eventsInfinite', sampleKey: groupKeys.network.eventsInfinite('network-1', { window: 'past', pageSize: 12 }).queryKey },
  { name: 'groupKeys.network.joinRequests', sampleKey: groupKeys.network.joinRequests('network-1').queryKey },
  { name: 'groupKeys.network.inviteNote', sampleKey: groupKeys.network.inviteNote('network-1').queryKey },
  { name: 'groupKeys.network.invitableMembers', sampleKey: groupKeys.network.invitableMembers('network-1', { q: ' Ada ' }).queryKey },
  { name: 'groupKeys.network.inviteEmailStatus', sampleKey: groupKeys.network.inviteEmailStatus('network-1', ' Ada@Example.com ').queryKey },
  { name: 'groupKeys.network.invitesPage', sampleKey: groupKeys.network.invitesPage('network-1', { page: 2, q: ' ada ' }).queryKey },
  { name: 'groupKeys.members', sampleKey: groupKeys.members('group-1', { search: 'ada', pageSize: 100 }).queryKey },
  { name: 'groupKeys.viewerRole', sampleKey: groupKeys.viewerRole('group-1').queryKey },
  { name: 'groupKeys.viewerRolesBatch', sampleKey: groupKeys.viewerRolesBatch(['b', 'a', 'c']).queryKey },
  { name: 'groupKeys.myPendingInvites', sampleKey: groupKeys.myPendingInvites().queryKey },
  { name: 'groupKeys.leaderForRequest', sampleKey: groupKeys.leaderForRequest('group-1', 'circle').queryKey },
);

// Re-export shared types consumers may need; helps avoid scattered
// `@leanin/shared/ids` imports for the common `UserId` cast in callers.
export type { GroupId, UserId };
